import type { FC } from 'react';
import { useUserStore } from '@Auth/store';
import AvatarMenu from '@components/AvatarMenu';
import { Show } from '@components/Show';
import Svg from '@components/Svg';
import Tooltip from '@components/Tooltip';
import PublishingOptions from '@ContractBuilder/components/PublishingOptions/PublishingOptions';
import { ShowCommentsAndActivitySection } from '@ContractBuilder/components/ShowCommentsAndActivitySection';
import { ShowPbqaEvalSection } from '@ContractBuilder/components/ShowPbqaEvalSection';
import SupportIconButton from '@ContractBuilder/contract-builder-header/components/ContactSupportIconButton';
import SaveCloseButton from '@ContractBuilder/contract-builder-header/components/SaveCloseButton';
import { LinkedDocsController } from '@ContractBuilder/contract-builder-header/modules/linked-docs/controller/LinkedDocsController';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import { useMultiSelectStore } from '@ContractBuilder/store/multi-select.store';
import { useUIStore } from '@ContractBuilder/store/ui.store';
import type { EntityData, PublishingOptionType } from '@ContractBuilder/types';
import { CheckValidation } from '@features/contract-validation/check-validation-panel/components/CheckValidation';
import { SyncDataButtonController } from '@features/sync-data-button';
import { TemplateValidationController } from '@features/template-validation';
import { canChange } from '@helpers/canChange';
import { ClipboardListIcon, DocumentSearchIcon, EyeOffIcon } from '@heroicons/react/outline';
import { useModalDispatch } from '@hooks/useModalProvider';
import type { RootResourceStatus } from '@root/@types/types';
import { isAdmin } from '@root/helpers/permissions';
import { isPPLFrontend } from '@root/src/utils/app-paths';
import { MODALS } from '@src/constants';
import { useDocumentLocation, useFeatureFlags } from '@src/hooks';
import clsx from 'clsx';
import { get } from 'lodash-es';

import { CircleIconPlaceholder, CircleIconWrapper } from '../../../components/CircleIconWrapper';

interface ContractToolsProps {
  isLoading: boolean;
  onStatusUpdate: (status: RootResourceStatus, shouldBypassDatapointValidation?: boolean) => Promise<void>;
  publishingOptions: PublishingOptionType[];
  submission: EntityData;
}

export const ContractTools: FC<ContractToolsProps> = ({ isLoading, onStatusUpdate, publishingOptions, submission }) => {
  const user = useUserStore(({ user }) => user);
  const { isContractView, isTemplate, isEndorsement, isSubmission } = useDocumentLocation();
  const { toggleShowHiddenFields, showHiddenFields, togglePDFPreview, isPDFPreviewOpen } = useUIStore();
  const isEditing = useBlockEditFormStore((state) => state.formValues?.id !== undefined);
  const isAnyMultiSelectActive = useMultiSelectStore(
    (state) => state.activePartId !== null && state.selectedBlockIds.size > 0,
  );
  const isFormDirty = useDatapointsStore((state) => state.isDirty);
  const { showModal } = useModalDispatch();

  const getFeatureFlagState = useFeatureFlags();
  const hasOpenSearchFeatureFlag = getFeatureFlagState('OpenSearch');
  const disableRecordCreation = getFeatureFlagState('DisableRecordCreation');

  const hasRenewals = get(submission, 'renewals.length', 0) > 0;

  const canChangeStatusAsAdmin = isAdmin(user) && !hasRenewals;
  const canChangeEntityStatus = canChange(submission.status, undefined, 'status-change') || canChangeStatusAsAdmin;

  const { activeEditingField } = useUIStore(({ activeEditingField }) => ({ activeEditingField }));
  const shouldDisablePublishingOptions =
    isLoading || !canChangeEntityStatus || isEditing || !!activeEditingField || isFormDirty || isAnyMultiSelectActive;

  const hasEndorsementsFeature = getFeatureFlagState('Endorsements');

  const handleOpenEndorsementDetailsModal = () => {
    return showModal(MODALS.CREATE_ENDORSEMENT, { isEdit: true });
  };

  const handleCloneEndorsement = () => {
    return showModal(hasOpenSearchFeatureFlag ? MODALS.CLONE_ENDORSEMENT_OPEN_SEARCH : MODALS.CLONE_ENDORSEMENT, {});
  };

  const handlePreviewPdf = () => {
    return togglePDFPreview();
  };

  return (
    <>
      <div className="flex items-center gap-x-3">
        <Show when={!isTemplate}>
          <Show when={isContractView}>
            <Tooltip content={`${showHiddenFields ? 'Hide' : 'Show'} hidden fields`}>
              <CircleIconWrapper
                data-testid="show-hidden-fields-button"
                icon={EyeOffIcon}
                isActive={showHiddenFields}
                onClick={toggleShowHiddenFields}
                wrapperClassName={clsx(showHiddenFields && 'hover:bg-primary-100')}
              />
            </Tooltip>
          </Show>
          <Show when={!isContractView}>
            <CircleIconPlaceholder />
          </Show>
        </Show>
        <SupportIconButton />
        <Tooltip content="Preview PDF">
          <CircleIconWrapper
            data-testid="preview-pdf-button"
            icon={DocumentSearchIcon}
            isActive={isPDFPreviewOpen}
            onClick={handlePreviewPdf}
          />
        </Tooltip>
        <Show when={!isPPLFrontend && isTemplate && ['DRAFT', 'REVIEW'].includes(submission.status)}>
          <TemplateValidationController />
        </Show>
        <Show when={!isPPLFrontend && isSubmission && ['DRAFT', 'REVIEW'].includes(submission.status)}>
          <CheckValidation />
        </Show>
        {/* Workflow order: validate → guidance → comments / activity. */}
        <ShowPbqaEvalSection />
        <ShowCommentsAndActivitySection />
        <Show when={isEndorsement && hasEndorsementsFeature}>
          <Tooltip content="Endorsement Details" disabled={isEditing}>
            <CircleIconWrapper
              data-testid="edit-endorsement-details"
              icon={ClipboardListIcon}
              isActive={false}
              onClick={handleOpenEndorsementDetailsModal}
              wrapperClassName={clsx(showHiddenFields && 'hover:bg-primary-100')}
            />
          </Tooltip>
        </Show>
        <Show when={isEndorsement && hasEndorsementsFeature && !disableRecordCreation}>
          <Tooltip content="Duplicate Endorsement">
            <CircleIconWrapper
              data-testid="clone-endorsement"
              icon={(props) => <Svg name="layers-plus" {...props} />}
              isActive={false}
              onClick={handleCloneEndorsement}
              wrapperClassName={clsx(showHiddenFields && 'hover:bg-primary-100')}
            />
          </Tooltip>
        </Show>
        <LinkedDocsController />
      </div>
      <Show when={isPPLFrontend ? isTemplate : true}>
        <PublishingOptions
          hasMaterialChanges={submission.hasMaterialChanges}
          isDisabled={shouldDisablePublishingOptions}
          isLoading={isLoading}
          onStatusUpdate={onStatusUpdate}
          publishingOptions={publishingOptions}
          status={submission.status}
        />
      </Show>
      <Show when={isPPLFrontend && !isTemplate}>
        <SyncDataButtonController />
        <SaveCloseButton />
      </Show>
      <AvatarMenu />
    </>
  );
};
